import { ReactNode } from "react";
import { styled } from "styled-components";

import { Alert, Button, colors, Modal, Spacer, Text } from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";

export interface MidExamUpdateWarningModalProps {
  useCase: keyof typeof CASES;
  onPositiveAction: VoidFunction;
  onNegativeAction: VoidFunction;
}

export const ConnectedMidExamUpdateWarningModal =
  create<MidExamUpdateWarningModalProps>((props) => {
    const modal = useModal();
    return <MidExamUpdateWarningModal {...props} visible={modal.visible} />;
  });

interface InnerProps extends MidExamUpdateWarningModalProps {
  visible?: boolean;
}

export function MidExamUpdateWarningModal(props: InnerProps) {
  const { useCase, onPositiveAction, onNegativeAction, visible } = props;
  const copy = CASES[useCase];
  return (
    <Modal.Root open={visible}>
      <Modal.Content withCloseButton={false}>
        <Container>
          <Modal.Title asChild>
            <Text kind="headingFour" textAlign="center">
              {copy.title}
            </Text>
          </Modal.Title>
          <Spacer spacing={24} />
          <Modal.Description asChild>
            <Alert severity="warning" title={copy.alertTitle}>
              {copy.alertBody}
            </Alert>
          </Modal.Description>
          <Spacer spacing={32} />
          <ButtonsContainer>
            <Button onClick={onNegativeAction}>Cancel</Button>
            <Button onClick={onPositiveAction} kind="secondary">
              {copy.positiveAction}
            </Button>
          </ButtonsContainer>
        </Container>
      </Modal.Content>
    </Modal.Root>
  );
}

interface Copy {
  title: ReactNode;
  alertTitle?: ReactNode;
  alertBody: ReactNode;
  positiveAction: ReactNode;
}

export const updateSettingsCopy: Copy = {
  title: <>Are you sure you want to make changes while the exam is underway?</>,
  alertBody: (
    <>Applying these changes may affect students who are already working</>
  ),
  positiveAction: "Update exam settings",
};

const updateInstructionsCopy: Copy = {
  title: (
    <>
      Are you sure you want to update the instructions while students are
      working?
    </>
  ),
  alertTitle: (
    <>Students will be notified but may not identify what has changed</>
  ),
  alertBody: (
    <>We recommend highlighting any changes clearly within the instructions</>
  ),
  positiveAction: "Update exam instructions",
};

const CASES = {
  updateSettings: updateSettingsCopy,
  updateInstructions: updateInstructionsCopy,
};

const Container = styled.div`
  max-width: 600px;
  display: flex;
  flex-direction: column;
  background-color: ${colors.white100};
  border-radius: 4px;
  padding: 56px 77px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;
